import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  HeartPulse,
  ShieldCheck,
  Stethoscope,
  Activity,
  Brain,
  Bone,
  Dumbbell,
  Baby,
  Wind,
  Sparkles,
  Target,
  Users,
  Clock,
  X,
  Phone,
} from "lucide-react";

const medicalServices = [
  {
    id: 1,
    title: "Specialist Medical Care",
    description:
      "Diagnosis, treatment, and consultation under the guidance of qualified medical professionals.",
    icon: Stethoscope,
    color: "blue",
  },
  {
    id: 2,
    title: "Comprehensive Patient Care",
    description:
      "Coordinated care with medical professionals, nurses, and staff to support patient needs.",
    icon: HeartPulse,
    color: "cyan",
  },
  {
    id: 3,
    title: "Stroke Rehabilitation",
    description:
      "Specialized rehabilitation support for stroke patients alongside appropriate medical care.",
    icon: Brain,
    color: "indigo",
  },
  {
    id: 4,
    title: "24-Hour Ambulance Service",
    description:
      "Emergency transportation support to help patients access essential medical services.",
    icon: Activity,
    color: "rose",
  },
];

const physiotherapyServices = [
  {
    id: 1,
    title: "Neurological Physiotherapy",
    subtitle: "Neurological Rehabilitation",
    description:
      "Rehabilitation support for neurological conditions and movement limitations.",
    icon: Brain,
    image:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=900&auto=format&fit=crop&q=80",
    services: [
      "Stroke and paralysis rehabilitation",
      "GBS and neurological conditions",
      "Spinal cord injury rehabilitation",
      "Balance and mobility training",
    ],
    gradient: "from-blue-600 to-indigo-500",
  },
  {
    id: 2,
    title: "Orthopedic & Musculoskeletal",
    subtitle: "Orthopedic Physiotherapy",
    description:
      "Movement-focused rehabilitation for musculoskeletal pain, injuries, and functional limitations.",
    icon: Bone,
    image:
      "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?w=900&auto=format&fit=crop&q=80",
    services: [
      "Back and neck pain",
      "Arthritis and joint pain",
      "Frozen shoulder",
      "Muscle and joint rehabilitation",
    ],
    gradient: "from-cyan-500 to-blue-500",
  },
  {
    id: 3,
    title: "Sports Rehabilitation",
    subtitle: "Sports Physiotherapy",
    description:
      "Rehabilitation and exercise support for athletes and individuals recovering from sports injuries.",
    icon: Dumbbell,
    image:
      "https://images.unsplash.com/photo-1538805060514-97d9cc17730c?w=900&auto=format&fit=crop&q=80",
    services: [
      "ACL, PCL, LCL and MCL injuries",
      "Sports injury rehabilitation",
      "Muscle strain recovery",
      "Strength and mobility training",
    ],
    gradient: "from-emerald-500 to-teal-400",
  },
  {
    id: 4,
    title: "Pediatric & Geriatric",
    subtitle: "Specialized Rehabilitation",
    description:
      "Individualized rehabilitation support for children, older adults, and patients with specific functional needs.",
    icon: Baby,
    image:
      "https://images.unsplash.com/photo-1576765608866-5b51046452be?w=900&auto=format&fit=crop&q=80",
    services: [
      "Developmental support",
      "Postural correction",
      "Balance and fall prevention",
      "Age-related muscle weakness",
    ],
    gradient: "from-violet-500 to-fuchsia-400",
  },
  {
    id: 5,
    title: "Gynecological Physiotherapy",
    subtitle: "Women's Rehabilitation",
    description:
      "Physiotherapy support for pelvic health, pregnancy-related functional concerns, and rehabilitation.",
    icon: HeartPulse,
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=900&auto=format&fit=crop&q=80",
    services: [
      "Postpartum rehabilitation",
      "Pelvic floor support",
      "Pregnancy-related discomfort",
      "Pelvic muscle strengthening",
    ],
    gradient: "from-rose-500 to-pink-400",
  },
  {
    id: 6,
    title: "Chest Physiotherapy",
    subtitle: "Respiratory Rehabilitation",
    description:
      "Respiratory rehabilitation support for selected pulmonary conditions and post-operative recovery.",
    icon: Wind,
    image:
      "https://images.unsplash.com/photo-1584515933487-779824d29309?w=900&auto=format&fit=crop&q=80",
    services: [
      "Breathing exercises",
      "Respiratory rehabilitation",
      "Support for selected pulmonary conditions",
      "Post-operative respiratory care",
    ],
    gradient: "from-sky-500 to-cyan-400",
  },
  {
    id: 7,
    title: "Fracture & Post-Operative",
    subtitle: "Recovery & Rehabilitation",
    description:
      "Functional rehabilitation after fractures and surgical procedures, based on clinical assessment.",
    icon: ShieldCheck,
    image:
      "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=900&auto=format&fit=crop&q=80",
    services: [
      "Post-fracture rehabilitation",
      "Joint stiffness management",
      "Post-operative recovery",
      "Functional movement training",
    ],
    gradient: "from-amber-500 to-orange-400",
  },
  {
    id: 8,
    title: "Speech & Language Therapy",
    subtitle: "Communication Support",
    description:
      "Support for speech, language, communication, and related rehabilitation needs.",
    icon: Users,
    image:
      "https://images.unsplash.com/photo-1584982751601-97dcc096659c?w=900&auto=format&fit=crop&q=80",
    services: [
      "Speech and language support",
      "Communication rehabilitation",
      "Selected neurological conditions",
      "Individualized therapy planning",
    ],
    gradient: "from-purple-500 to-violet-400",
  },
];

const goals = [
  {
    icon: HeartPulse,
    title: "Patient-Centered Care",
    description:
      "Support patients through compassionate and coordinated healthcare services.",
  },
  {
    icon: Target,
    title: "Functional Recovery",
    description:
      "Promote improved mobility, independence, and quality of life.",
  },
  {
    icon: ShieldCheck,
    title: "Professional Service",
    description:
      "Encourage evidence-informed care and collaboration with qualified professionals.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

function SectionHeading({ eyebrow, title, description }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="mx-auto max-w-3xl text-center"
    >
      <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.18em] text-blue-600">
        <Sparkles size={14} />
        {eyebrow}
      </span>

      <h2 className="mt-5 text-3xl font-black leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
        {title}
      </h2>

      <p className="mt-5 text-base leading-8 text-slate-600">
        {description}
      </p>
    </motion.div>
  );
}

function MedicalServicesSection() {
  const [selectedService, setSelectedService] = useState(null);

  return (
    <section className="relative overflow-hidden bg-white">
      {/* =====================================================
          SECTION 1: MEDICAL SERVICES INTRODUCTION
      ====================================================== */}
      <div className="relative bg-gradient-to-br from-slate-50 via-white to-blue-50 px-5 py-20 lg:px-8 lg:py-28">
        <div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-blue-100/50 blur-3xl" />
        <div className="absolute -right-20 bottom-0 h-96 w-96 rounded-full bg-cyan-100/40 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-2">
          {/* Content */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.16em] text-blue-700">
              <Stethoscope size={14} />
              Medical Services
            </span>

            <h2 className="mt-6 text-4xl font-black leading-[1.12] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Care That Supports
              <span className="block text-blue-600">Every Recovery.</span>
            </h2>

            <p className="mt-6 max-w-xl text-base leading-8 text-slate-600 sm:text-lg">
              Our medical services are designed to support patients through
              professional consultation, coordinated care, and rehabilitation
              support. Every patient's needs are different, and care should be
              guided by qualified healthcare professionals.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#physiotherapy-services"
                className="group flex items-center gap-3 rounded-full bg-blue-600 px-6 py-4 font-bold text-white shadow-xl shadow-blue-200 transition hover:bg-blue-700"
              >
                Explore Our Services
                <ArrowRight
                  size={18}
                  className="transition group-hover:translate-x-1"
                />
              </a>

              <a
                href="#contact"
                className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-4 font-bold text-slate-700 transition hover:border-blue-300 hover:text-blue-600"
              >
                <Phone size={18} />
                Contact Us
              </a>
            </div>

            {/* Trust points */}
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm font-semibold text-slate-600">
              <span className="flex items-center gap-2">
                <CheckCircle2 size={18} className="text-emerald-500" />
                Professional Care
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 size={18} className="text-emerald-500" />
                Patient Focused
              </span>
            </div>
          </motion.div>

          {/* Image + Floating Cards */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative mx-auto w-full max-w-xl"
          >
            <div className="absolute -inset-5 rounded-[3rem] bg-gradient-to-br from-blue-200/50 to-cyan-200/30 blur-2xl" />

            <div className="relative overflow-hidden rounded-[2.5rem] border-8 border-white shadow-2xl shadow-blue-200/60">
              <img
                src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&auto=format&fit=crop&q=85"
                alt="Medical professional providing patient care"
                className="h-[430px] w-full object-cover sm:h-[500px]"
                loading="lazy"
              />

              <div className="absolute inset-x-4 bottom-4 rounded-3xl border border-white/40 bg-white/90 p-4 shadow-xl backdrop-blur-md">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-600">
                    <ShieldCheck size={25} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">
                      Coordinated Healthcare
                    </p>
                    <p className="text-xs text-slate-500">
                      Supporting your care journey
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -right-3 top-10 hidden rounded-2xl border border-white bg-white p-4 shadow-xl sm:block"
            >
              <HeartPulse className="mb-1 text-blue-600" size={25} />
              <p className="text-xs font-bold text-slate-700">
                Patient Care
              </p>
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{
                duration: 4.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -left-5 bottom-32 hidden rounded-2xl border border-white bg-white p-4 shadow-xl sm:block"
            >
              <Stethoscope className="mb-1 text-cyan-600" size={25} />
              <p className="text-xs font-bold text-slate-700">
                Expert Guidance
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Medical Feature Cards */}
        <div className="relative mx-auto mt-20 max-w-7xl">
          <SectionHeading
            eyebrow="Our Medical Support"
            title="Comprehensive Medical Services"
            description="Explore the areas of medical and rehabilitation support offered by our healthcare team."
          />

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {medicalServices.map((service, index) => {
              const Icon = service.icon;

              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -7 }}
                  className="group rounded-3xl border border-slate-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-xl hover:shadow-blue-100/60"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition group-hover:bg-blue-600 group-hover:text-white">
                    <Icon size={28} />
                  </div>

                  <h3 className="mt-6 text-lg font-extrabold text-slate-900">
                    {service.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-500">
                    {service.description}
                  </p>

                  <div className="mt-5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-blue-600">
                    Quality Care
                    <ArrowRight size={15} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* =====================================================
          SECTION 2: PHYSIOTHERAPY SERVICES
      ====================================================== */}
      <div
        id="physiotherapy-services"
        className="relative bg-gradient-to-b from-white to-slate-50 px-5 py-20 lg:px-8 lg:py-28"
      >
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="Physiotherapy Services"
            title="Specialized Rehabilitation for Every Need"
            description="From neurological rehabilitation to orthopedic recovery, our services support functional movement and personalized rehabilitation goals."
          />

          <div className="mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
            {physiotherapyServices.map((service, index) => {
              const Icon = service.icon;

              return (
                <motion.article
                  key={service.id}
                  initial={{ opacity: 0, y: 35 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{
                    duration: 0.5,
                    delay: (index % 4) * 0.08,
                  }}
                  whileHover={{ y: -8 }}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-shadow hover:shadow-2xl hover:shadow-blue-100/60"
                >
                  {/* Image */}
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />

                    <div className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-2xl border border-white/30 bg-white/90 text-blue-600 shadow-lg backdrop-blur-sm">
                      <Icon size={22} />
                    </div>

                    <span className="absolute bottom-4 left-5 right-4 text-xs font-bold uppercase tracking-wider text-white/90">
                      Rehabilitation Care
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-xs font-extrabold uppercase tracking-wider text-blue-600">
                      {service.subtitle}
                    </p>

                    <h3 className="mt-2 text-xl font-extrabold leading-snug text-slate-900">
                      {service.title}
                    </h3>

                    <p className="mt-3 text-sm leading-7 text-slate-500">
                      {service.description}
                    </p>

                    <div className="mt-5 space-y-2.5">
                      {service.services.slice(0, 3).map((item) => (
                        <div
                          key={item}
                          className="flex items-start gap-2 text-sm text-slate-600"
                        >
                          <CheckCircle2
                            size={16}
                            className="mt-0.5 shrink-0 text-blue-500"
                          />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-auto pt-6">
                      <button
                        onClick={() => setSelectedService(service)}
                        className="flex w-full items-center justify-between rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700 transition hover:bg-blue-600 hover:text-white"
                      >
                        Explore Service
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </div>

      {/* =====================================================
          SECTION 3: OUR GOALS
      ====================================================== */}
      <div className="relative overflow-hidden bg-slate-900 px-5 py-20 text-white lg:px-8 lg:py-28">
        <div className="absolute -right-40 -top-40 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-cyan-600/10 blur-3xl" />

        <div className="relative mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="Our Goals"
            title="Committed to Better Patient Outcomes"
            description="Our vision is to support patients with compassionate care, rehabilitation, and professional healthcare services."
          />

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {goals.map((goal, index) => {
              const Icon = goal.icon;

              return (
                <motion.div
                  key={goal.title}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.12 }}
                  className="rounded-3xl border border-white/10 bg-white/5 p-7 text-center backdrop-blur-md transition hover:bg-white/10"
                >
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-cyan-400/15 text-cyan-300">
                    <Icon size={30} />
                  </div>

                  <h3 className="mt-5 text-xl font-bold">{goal.title}</h3>

                  <p className="mt-3 text-sm leading-7 text-slate-300">
                    {goal.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto mt-12 max-w-4xl rounded-3xl border border-cyan-300/20 bg-gradient-to-r from-blue-600/30 to-cyan-500/20 p-7 text-center sm:p-10"
          >
            <h3 className="text-2xl font-black sm:text-3xl">
              Specialized Care for Children & Older Adults
            </h3>

            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
              We aim to support specialized physiotherapy needs, including
              pediatric rehabilitation, geriatric care, and functional
              rehabilitation for patients with long-term conditions.
            </p>
          </motion.div>
        </div>
      </div>

      {/* =====================================================
          SECTION 4: CALL TO ACTION
      ====================================================== */}
      <div
        id="contact"
        className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-cyan-500 px-5 py-20 lg:px-8 lg:py-24"
      >
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full border-[40px] border-white/10" />
        <div className="absolute -bottom-40 -left-20 h-80 w-80 rounded-full border-[50px] border-white/10" />

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative mx-auto max-w-4xl text-center text-white"
        >
          <span className="text-sm font-extrabold uppercase tracking-[0.2em] text-blue-100">
            Contact Our Team
          </span>

          <h2 className="mt-4 text-3xl font-black sm:text-5xl">
            Your Health. Our Commitment.
          </h2>

          <p className="mx-auto mt-5 max-w-2xl leading-8 text-blue-50">
            Contact our team to learn about our services and arrange an
            appointment with a qualified healthcare professional.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="tel:+8801000000000"
              className="flex items-center gap-3 rounded-full bg-white px-7 py-4 font-bold text-blue-700 shadow-xl transition hover:bg-blue-50"
            >
              <Phone size={18} />
              Call for Appointment
            </a>

            <a
              href="mailto:info@example.com"
              className="flex items-center gap-3 rounded-full border border-white/40 bg-white/10 px-7 py-4 font-bold text-white transition hover:bg-white/20"
            >
              Email Us
              <ArrowRight size={18} />
            </a>
          </div>
        </motion.div>
      </div>

      {/* =====================================================
          SECTION 5: SERVICE DETAILS MODAL
      ====================================================== */}
      {selectedService && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => setSelectedService(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white shadow-2xl"
          >
            <div className="relative h-52">
              <img
                src={selectedService.image}
                alt={selectedService.title}
                className="h-full w-full object-cover"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />

              <button
                onClick={() => setSelectedService(null)}
                className="absolute right-4 top-4 rounded-full bg-white/90 p-2 text-slate-700 shadow-lg transition hover:bg-white"
                aria-label="Close service details"
              >
                <X size={20} />
              </button>

              <h3 className="absolute bottom-5 left-6 right-6 text-2xl font-extrabold text-white">
                {selectedService.title}
              </h3>
            </div>

            <div className="p-6 sm:p-8">
              <p className="leading-7 text-slate-600">
                {selectedService.description}
              </p>

              <h4 className="mt-6 font-bold text-slate-900">
                Areas of Care
              </h4>

              <ul className="mt-4 space-y-3">
                {selectedService.services.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-sm text-slate-600"
                  >
                    <CheckCircle2
                      size={18}
                      className="mt-0.5 shrink-0 text-blue-600"
                    />
                    {item}
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                onClick={() => setSelectedService(null)}
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-4 font-bold text-white transition hover:bg-blue-700"
              >
                Contact for Appointment
                <ArrowRight size={18} />
              </a>

              <p className="mt-4 flex items-start gap-2 text-xs leading-5 text-slate-400">
                <Clock size={14} className="mt-0.5 shrink-0" />
                Treatment suitability and session plans are determined through
                professional assessment.
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
}

export default MedicalServicesSection;